/**
 * WebGL context registry.
 *
 * There is no browser API to enumerate live WebGL contexts, so we instrument
 * creation: `HTMLCanvasElement.prototype.getContext` is wrapped once at boot and
 * every WebGL context it returns is recorded behind a `WeakRef`.
 *
 * Why this exists: removing a canvas from the DOM does NOT release its WebGL
 * context. A detached-but-alive context still counts against Chromium's live
 * context limit, and when that limit is hit the browser evicts the oldest context
 * ("Too many active WebGL contexts. Oldest context will be lost.") — which is one
 * way the map goes black. Counting canvases cannot see that; this can.
 *
 * The metric that matters is {@link WebGLContextStats.glContextsOrphaned}: a context
 * that is still alive while its canvas is gone from the document. Each one is
 * attributed to the map UI that created it via the nearest `data-map-surface`
 * ancestor — see {@link WebGLContextStats.glOrphanSurfaces}.
 *
 * SAFETY: this patches a DOM prototype used by every canvas in the app, so it is
 * written to be incapable of breaking rendering:
 *   - the real `getContext` is called first and its result returned unconditionally;
 *     all bookkeeping happens afterwards inside try/catch
 *   - installation is idempotent and itself fully guarded
 *   - unsupported environments (no `WeakRef`) are a no-op
 *   - the registry is capped and self-pruning, so it cannot grow without bound in a
 *     24/7 session
 *   - `WeakRef` is required, not incidental: holding contexts strongly would keep
 *     them alive and manufacture the very leak we are trying to observe
 *
 * Nothing is attached to `window` in production; the inspection helpers are gated to
 * development builds.
 */

/** Max tracked entries. Dead entries are pruned first; if still full, the oldest
 * are dropped. Bounds memory in long-running sessions. */
const MAX_TRACKED_CONTEXTS = 200;
/** There are only a handful of map surfaces, so this bound is generous by design. */
const MAX_REPORTED_SURFACES = 5;

/** Minimal shape of WeakRef. Declared locally rather than relying on the global
 * `WeakRef` type so this compiles under every tsconfig `lib` in the repo — we
 * feature-detect the constructor at runtime regardless. */
type WeakRefLike<T extends object> = { deref: () => T | undefined };
type WeakRefConstructor = new <T extends object>(target: T) => WeakRefLike<T>;

function getWeakRefConstructor(): WeakRefConstructor | undefined {
    const ctor = (globalThis as { WeakRef?: WeakRefConstructor }).WeakRef;
    return typeof ctor === 'function' ? ctor : undefined;
}

type TrackedContext = {
    id: number;
    contextType: string;
    contextRef: WeakRefLike<object>;
    canvasRef: WeakRefLike<HTMLCanvasElement>;
    createdAtMs: number;
    /** Which map UI owns this context (see {@link resolveSurface}). Memoized once
     * resolved; stays undefined for non-map canvases. */
    surface?: string;
};

export type WebGLContextStats = {
    /** Contexts created since install (capped by {@link MAX_TRACKED_CONTEXTS}). */
    glContextsTracked: number;
    /** Alive and not lost. */
    glContextsLive: number;
    /** Alive but reporting `isContextLost()`. */
    glContextsLost: number;
    /** Garbage collected — the healthy outcome. */
    glContextsCollected: number;
    /** THE LEAK SIGNAL: alive, not lost, but the canvas is no longer in the document. */
    glContextsOrphaned: number;
    /** Age of the oldest orphaned context, to distinguish a transient teardown
     * window from a genuine leak. */
    glOldestOrphanAgeMs?: number;
    /** WHICH MAP UI LEAKED: `data-map-surface` of orphaned contexts, most frequent
     * first, e.g. `["9x fr-event-preview", "6x location-select"]`. Attribution is by
     * surface rather than stack trace because Esri, not our code, calls `getContext`:
     * a creation stack always bottoms out in the same minified Esri frame. */
    glOrphanSurfaces?: string[];
    /** Live (not lost, not orphaned) context count per map surface. */
    glContextsBySurface?: string[];
};

const EMPTY_STATS: WebGLContextStats = {
    glContextsTracked: 0,
    glContextsLive: 0,
    glContextsLost: 0,
    glContextsCollected: 0,
    glContextsOrphaned: 0,
};

let installed = false;
let nextId = 1;
/** Whether this page has ever been handed a WebGL2 context. Never resets. */
let createdWebGL2Context = false;
let registry: TrackedContext[] = [];
/** Counts entries dropped from the registry so totals stay honest after pruning. */
let droppedCollected = 0;

function isWebGLContextType(type: unknown): boolean {
    return typeof type === 'string' && /webgl/i.test(type);
}

function isContextLost(context: object): boolean {
    const maybe = context as { isContextLost?: () => boolean };
    if (typeof maybe.isContextLost !== 'function') {
        return false;
    }
    try {
        return maybe.isContextLost() === true;
    } catch {
        return false;
    }
}

/**
 * Which map UI owns a canvas, read from the nearest `data-map-surface` ancestor.
 *
 * DOM-derived rather than timing-derived on purpose: Esri creates its canvases and
 * asks for their contexts on its own schedule, so an ambient "currently constructing"
 * variable set around `new MapView(...)` would attribute nothing.
 *
 * Resolution is lazy and memoized. A canvas is not always in the document at
 * `getContext` time, so a first attempt that finds nothing is retried on later reads
 * (while the canvas is still attached) rather than being cached as unknown. Only ever
 * returns one of our own literal surface names — never user data.
 */
function resolveSurface(entry: TrackedContext): string | undefined {
    if (entry.surface !== undefined) {
        return entry.surface;
    }
    try {
        const canvas = entry.canvasRef.deref();
        if (!canvas || typeof canvas.closest !== 'function') {
            return undefined;
        }
        const surface = canvas.closest('[data-map-surface]')?.getAttribute('data-map-surface');
        if (surface) {
            entry.surface = surface;
        }
        return entry.surface;
    } catch {
        return undefined;
    }
}

function summarizeCounts(counts: Map<string, number>, limit: number): string[] {
    return Array.from(counts.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, limit)
        .map(([key, count]) => `${count}x ${key}`);
}

/** Drop entries whose context has been collected; if still at cap, drop oldest. */
function pruneRegistry(): void {
    const before = registry.length;
    const alive = registry.filter((entry) => entry.contextRef.deref() !== undefined);
    droppedCollected += before - alive.length;
    registry = alive;
    if (registry.length >= MAX_TRACKED_CONTEXTS) {
        registry = registry.slice(registry.length - MAX_TRACKED_CONTEXTS + 1);
    }
}

function record(canvas: HTMLCanvasElement, context: object, contextType: string): void {
    if (contextType.toLowerCase() === 'webgl2') {
        createdWebGL2Context = true;
    }
    const WeakRefCtor = getWeakRefConstructor();
    if (!WeakRefCtor) {
        return;
    }
    // Same canvas + same type returns the same context object, so only track once.
    const tagged = context as { __m43GlContextId?: number };
    if (tagged.__m43GlContextId !== undefined) {
        return;
    }
    if (registry.length >= MAX_TRACKED_CONTEXTS) {
        pruneRegistry();
    }
    tagged.__m43GlContextId = nextId++;
    const entry: TrackedContext = {
        id: tagged.__m43GlContextId,
        contextType,
        contextRef: new WeakRefCtor(context),
        canvasRef: new WeakRefCtor(canvas),
        createdAtMs: Date.now(),
    };
    registry.push(entry);
    // Attribute now if the canvas is already in the document; `resolveSurface` retries
    // on later reads when it is not, so a context Esri asks for before appending its
    // canvas is not permanently stuck as unattributed.
    resolveSurface(entry);
}

/**
 * Wrap `getContext` once. Safe to call more than once and safe to call in any
 * environment; failures leave the app untouched.
 */
export function installWebGLContextTracking(): void {
    try {
        if (installed) {
            return;
        }
        if (
            !getWeakRefConstructor() ||
            typeof HTMLCanvasElement === 'undefined' ||
            typeof HTMLCanvasElement.prototype?.getContext !== 'function'
        ) {
            return;
        }

        const original = HTMLCanvasElement.prototype.getContext;

        const patchedGetContext = function (this: HTMLCanvasElement, ...args: unknown[]) {
            // Call through FIRST and return unconditionally: bookkeeping must never
            // be able to change or break what the caller gets back.
            const context = (original as unknown as (...a: unknown[]) => unknown).apply(this, args);
            try {
                if (context && typeof context === 'object' && isWebGLContextType(args[0])) {
                    record(this, context as object, String(args[0]));
                }
            } catch {
                // Diagnostics must never interfere with rendering.
            }
            return context;
        };

        HTMLCanvasElement.prototype.getContext =
            patchedGetContext as typeof HTMLCanvasElement.prototype.getContext;
        installed = true;
        exposeDevHelpers();
    } catch {
        // Never let instrumentation stop the app from booting.
    }
}

/**
 * Force-release orphaned contexts — alive, not already lost, and whose canvas has
 * left the document — via `WEBGL_lose_context`. Returns how many were released.
 *
 * Restricted to *orphans* on purpose: a context whose canvas is still attached may
 * belong to a map the user is currently looking at (two `location-select` maps can be
 * open at once), and releasing that would black out a working map. After
 * `view.destroy()` Esri detaches its canvases, so a torn-down map's contexts match
 * and a live sibling's do not.
 *
 * Pass `surface` to limit the release to one map UI. Never throws.
 */
export function releaseOrphanedContexts(surface?: string): number {
    let released = 0;
    try {
        for (const entry of registry) {
            const context = entry.contextRef.deref();
            if (!context || isContextLost(context)) {
                continue;
            }
            const canvas = entry.canvasRef.deref();
            if (canvas && canvas.isConnected) {
                continue;
            }
            if (surface !== undefined && resolveSurface(entry) !== surface) {
                continue;
            }
            const gl = context as {
                getExtension?: (name: string) => { loseContext?: () => void } | null;
            };
            try {
                gl.getExtension?.('WEBGL_lose_context')?.loseContext?.();
                released++;
            } catch {
                // Best effort — one context refusing to release must not stop the rest.
            }
        }
    } catch {
        // Releasing contexts is an optimisation, never a correctness requirement.
    }
    return released;
}

/**
 * Whether this page has ever been given a WebGL2 context. When a map later fails to get
 * one, this is what separates "this machine can't do WebGL2" from "it could, and now
 * can't". False when tracking isn't installed. Never throws.
 */
export function hasCreatedWebGL2Context(): boolean {
    return createdWebGL2Context;
}

export type GpuIdentity = {
    /** e.g. "Google Inc. (Intel)" */
    gpuVendor?: string;
    /** e.g. "ANGLE (Intel, Intel(R) Iris(R) Xe Graphics (0x000046A8) Direct3D11 vs_5_0
     * ps_5_0, D3D11-31.0.101.4255)" — the GPU model and, on Windows, its driver version. */
    gpuRenderer?: string;
};

let gpuIdentity: GpuIdentity | undefined;

/**
 * The GPU and driver the browser is rendering with.
 *
 * Black maps vary 40x by department on identical code, and the machines we have
 * diagnostics for share one GPU and a 2023 driver, so the driver is a live suspect. This
 * puts the answer on every map event instead of on the few with diagnostic context.
 * Hardware and driver only — no user, location or case data.
 *
 * Read from a context the app already holds, never by creating one (that would cost a
 * context, and can fail exactly when we most want the answer). Cached after the first
 * successful read, so it is still available once every context on the page is lost.
 * Returns `{}` until a live context exists, or when the browser withholds the strings.
 * Never throws.
 */
export function getGpuIdentity(): GpuIdentity {
    if (gpuIdentity) {
        return gpuIdentity;
    }
    try {
        for (const entry of registry) {
            const context = entry.contextRef.deref();
            if (!context || isContextLost(context)) {
                continue;
            }
            const gl = context as {
                getExtension?: (name: string) => unknown;
                getParameter?: (parameter: number) => unknown;
            };
            const extension = gl.getExtension?.('WEBGL_debug_renderer_info') as
                | { UNMASKED_VENDOR_WEBGL: number; UNMASKED_RENDERER_WEBGL: number }
                | null
                | undefined;
            if (!extension) {
                // The browser withholds it. That won't change, so don't ask again.
                gpuIdentity = {};
                return gpuIdentity;
            }
            const vendor = gl.getParameter?.(extension.UNMASKED_VENDOR_WEBGL);
            const renderer = gl.getParameter?.(extension.UNMASKED_RENDERER_WEBGL);
            gpuIdentity = {
                ...(typeof vendor === 'string' ? { gpuVendor: vendor } : {}),
                ...(typeof renderer === 'string' ? { gpuRenderer: renderer } : {}),
            };
            return gpuIdentity;
        }
    } catch {
        // Diagnostics must never interfere with rendering.
    }
    return {};
}

/* eslint-disable no-console -- dev-only helpers below; console IS their interface */

type ContextRow = {
    id: number;
    type: string;
    surface: string;
    state: 'live' | 'LOST';
    canvas: 'attached' | 'DETACHED' | 'gc';
    size: string;
    ageS: number;
};

/** Per-context detail for the dev console table. */
function buildContextRows(): ContextRow[] {
    const now = Date.now();
    const rows: ContextRow[] = [];
    for (const entry of registry) {
        const context = entry.contextRef.deref();
        if (!context) {
            continue;
        }
        const canvas = entry.canvasRef.deref();
        rows.push({
            id: entry.id,
            type: entry.contextType,
            surface: resolveSurface(entry) ?? '-',
            state: isContextLost(context) ? 'LOST' : 'live',
            canvas: canvas ? (canvas.isConnected ? 'attached' : 'DETACHED') : 'gc',
            size: canvas ? `${canvas.width}x${canvas.height}` : '-',
            ageS: Math.round((now - entry.createdAtMs) / 1000),
        });
    }
    return rows;
}

/**
 * In development only, expose an inspection helper so context health can be poked
 * at from the console. Never attached in production builds.
 */
function exposeDevHelpers(): void {
    if (process.env.NODE_ENV !== 'development' || typeof window === 'undefined') {
        return;
    }
    const globalWindow = window as unknown as { __glTrack?: unknown };
    if (globalWindow.__glTrack) {
        return;
    }
    globalWindow.__glTrack = {
        /** Raw stats object — same fields that ride along on map episode logs. */
        stats: getWebGLContextStats,
        /** Human-readable summary plus a per-context table. */
        report() {
            const stats = getWebGLContextStats();
            console.log(
                `[glTrack] tracked=${stats.glContextsTracked} live=${stats.glContextsLive} ` +
                    `lost=${stats.glContextsLost} collected=${stats.glContextsCollected} ` +
                    `ORPHANED(live but detached)=${stats.glContextsOrphaned}`
            );
            if (stats.glOrphanSurfaces?.length) {
                console.log('[glTrack] orphan surfaces:', stats.glOrphanSurfaces);
            }
            console.table(buildContextRows());
            return stats;
        },
        /** Release orphaned contexts by hand. If this relieves the "too many active
         * WebGL contexts" pressure, the leak is confirmed and explicit teardown is
         * the fix. Optionally scoped to one `data-map-surface`. */
        reapOrphans(surface?: string) {
            const released = releaseOrphanedContexts(surface);
            console.log(`[glTrack] released ${released} orphaned context(s)`);
            return released;
        },
    };
    console.log('[glTrack] dev helper ready — run window.__glTrack.report()');
}

/** Snapshot of context health. Returns zeroed stats if anything goes wrong. */
export function getWebGLContextStats(): WebGLContextStats {
    try {
        if (!installed) {
            return EMPTY_STATS;
        }
        let live = 0;
        let lost = 0;
        let collected = droppedCollected;
        let orphaned = 0;
        let oldestOrphanAgeMs: number | undefined;
        const orphanSurfaceCounts = new Map<string, number>();
        const liveSurfaceCounts = new Map<string, number>();
        const now = Date.now();

        for (const entry of registry) {
            const context = entry.contextRef.deref();
            if (!context) {
                collected++;
                continue;
            }
            if (isContextLost(context)) {
                lost++;
                continue;
            }
            live++;
            const canvas = entry.canvasRef.deref();
            const attached = !!canvas && canvas.isConnected;
            const surface = resolveSurface(entry);
            if (!attached) {
                orphaned++;
                const age = now - entry.createdAtMs;
                if (oldestOrphanAgeMs === undefined || age > oldestOrphanAgeMs) {
                    oldestOrphanAgeMs = age;
                }
                if (surface) {
                    orphanSurfaceCounts.set(surface, (orphanSurfaceCounts.get(surface) ?? 0) + 1);
                }
            } else if (surface) {
                liveSurfaceCounts.set(surface, (liveSurfaceCounts.get(surface) ?? 0) + 1);
            }
        }

        const stats: WebGLContextStats = {
            glContextsTracked: nextId - 1,
            glContextsLive: live,
            glContextsLost: lost,
            glContextsCollected: collected,
            glContextsOrphaned: orphaned,
        };
        if (oldestOrphanAgeMs !== undefined) {
            stats.glOldestOrphanAgeMs = oldestOrphanAgeMs;
        }
        if (orphanSurfaceCounts.size > 0) {
            stats.glOrphanSurfaces = summarizeCounts(orphanSurfaceCounts, MAX_REPORTED_SURFACES);
        }
        if (liveSurfaceCounts.size > 0) {
            stats.glContextsBySurface = summarizeCounts(liveSurfaceCounts, MAX_REPORTED_SURFACES);
        }
        return stats;
    } catch {
        return EMPTY_STATS;
    }
}

